'use client';

/**
 * ConsentReset — small client island on the privacy page. Shows the stored
 * consent choice and lets the visitor withdraw it: the localStorage entry is
 * removed and the consent bar reopens immediately.
 */

import { useEffect, useState } from 'react';
import { clearConsent, getConsent } from '@/analytics/gate';
import { useLang } from '@/i18n/client';
import type { Lang } from '@/i18n/config';
import styles from './ConsentReset.module.css';

type Status = 'reading' | 'none' | 'essential' | 'analytics' | 'cleared';

const copy: Record<Lang, { prefix: string; reset: string; line: Record<Status, string> }> = {
  en: {
    prefix: 'stored choice: ',
    reset: 'Reset consent choice',
    line: {
      reading: 'reading…',
      none: 'no choice stored — the consent bar will offer one',
      essential: 'essential only — no analytics load',
      analytics: 'analytics allowed',
      cleared: 'cleared — the consent bar has reopened',
    },
  },
  ru: {
    prefix: 'сохранённый выбор: ',
    reset: 'Сбросить выбор согласия',
    line: {
      reading: 'чтение…',
      none: 'выбор не сохранён — панель согласия предложит его',
      essential: 'только необходимое — аналитика не загружается',
      analytics: 'аналитика разрешена',
      cleared: 'сброшено — панель согласия открылась снова',
    },
  },
};

export default function ConsentReset() {
  const lang = useLang();
  const t = copy[lang];
  const [status, setStatus] = useState<Status>('reading');

  useEffect(() => {
    const c = getConsent();
    setStatus(c === null ? 'none' : c.analytics ? 'analytics' : 'essential');
  }, []);

  const reset = () => {
    clearConsent();
    setStatus('cleared');
  };

  return (
    <div className={styles.box}>
      <p className={`machine machine--sm ${styles.status}`}>
        {t.prefix}
        {t.line[status]}
      </p>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={reset}
        disabled={status === 'none' || status === 'cleared'}
      >
        {t.reset}
      </button>
    </div>
  );
}
